import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  Trophy, Star, Users, Sparkles, LogOut, Flame, Award, 
  ChevronRight, Calendar, HelpCircle, FolderOpen, Target, 
  Briefcase, CheckCircle, Brain, MapPin, Zap, Play, Bell, Lightbulb, BookOpen, MessageSquare
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Progress } from '@/components/ui/progress';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Badge } from '@/components/ui/badge';
import BirthdayAnimation, { useBirthdayCheck } from './BirthdayAnimation';
import NotificationCenter from './NotificationCenter';
import ProfileCompletionModal from './ProfileCompletionModal';
import NotificationBell from './NotificationBell';
import GoalReminders from './GoalReminders';
import MandatoryCheckinModal from './MandatoryCheckinModal';
import { useTheme } from '@/contexts/ThemeContext';

// Default YAS Logo
const DEFAULT_LOGO = '/yas-logo.png';

// Themed Dashboard Component - Uses program's theme settings
const ThemedYouthDashboard = ({ user, setUser }) => {
  const navigate = useNavigate();
  const { colors, mode, currentTemplate, getLogo, programName } = useTheme();
  const [loading, setLoading] = useState(true);
  const [weeklyStatus, setWeeklyStatus] = useState({ checkin: false });
  const [assessments, setAssessments] = useState({ pre: false, post: false });
  const [aiGuidance, setAiGuidance] = useState(null);
  const [weeklyTrends, setWeeklyTrends] = useState(null);
  const [staleCourses, setStaleCourses] = useState([]);
  
  // Birthday check hook
  const { isBirthday, userName, dismissBirthday } = useBirthdayCheck();

  useEffect(() => {
    fetchDashboardData();
    fetchAIGuidance();
    fetchWeeklyTrends();
    fetchStaleCourses();
    setLoading(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchWeeklyTrends = async () => {
    try {
      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/ai/my-weekly-trends`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
      });
      if (res.ok) setWeeklyTrends(await res.json());
    } catch (e) {}
  };

  const fetchStaleCourses = async () => {
    try {
      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/courses/available`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
      });
      if (res.ok) {
        const courses = await res.json();
        const threeDaysAgo = new Date(Date.now() - 3 * 24 * 60 * 60 * 1000).toISOString();
        const stale = courses.filter(c => c.current_module > 0 && !c.completed && c.updated_at && c.updated_at < threeDaysAgo);
        setStaleCourses(stale.slice(0, 2));
      }
    } catch (e) {}
  };

  const fetchAIGuidance = async () => {
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/ai/guidance?section=home`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) setAiGuidance(await res.json());
    } catch (e) { console.error(e); }
  };

  const fetchDashboardData = async () => {
    const token = localStorage.getItem('token');
    try {
      const [checkinsRes, assessmentsRes] = await Promise.all([
        fetch(`${process.env.REACT_APP_BACKEND_URL}/api/checkins/my`, {
          headers: { Authorization: `Bearer ${token}` }
        }),
        fetch(`${process.env.REACT_APP_BACKEND_URL}/api/assessments/my`, {
          headers: { Authorization: `Bearer ${token}` }
        })
      ]);

      if (checkinsRes.ok) {
        const checkins = await checkinsRes.json();
        const thisWeek = Math.floor((Date.now() - new Date('2024-06-01').getTime()) / (1000 * 60 * 60 * 24 * 7)) + 1;
        const hasCheckin = checkins.some(c => c.week_number === thisWeek);
        setWeeklyStatus(prev => ({ ...prev, checkin: hasCheckin }));
      }

      if (assessmentsRes.ok) {
        const assessmentData = await assessmentsRes.json();
        const hasPre = assessmentData.some(a => a.assessment_type === 'pre');
        const hasPost = assessmentData.some(a => a.assessment_type === 'post');
        setAssessments({ pre: hasPre, post: hasPost });
      }
    } catch (error) {
      console.error('Error fetching dashboard data:', error);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('token');
    setUser(null);
    navigate('/login');
  };

  const template = currentTemplate;
  const logo = getLogo() || DEFAULT_LOGO;

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ backgroundColor: colors.background }}>
        <div className="animate-spin w-8 h-8 border-4 border-t-transparent rounded-full" style={{ borderColor: colors.primary }} />
      </div>
    );
  }

  // Template-specific style helpers
  const getCardStyle = (rotate = false) => {
    const base = {
      backgroundColor: colors.surface,
      borderRadius: template === 'smooth_vibes' ? '1.5rem' : template === 'bold_energy' ? '1.5rem' : '1rem',
    };
    
    if (template === 'street_smart' || template === 'bold_energy') {
      base.border = `2px solid ${colors.text}`;
      base.boxShadow = '4px 4px 0px 0px rgba(0,0,0,0.9)';
    } else if (template === 'smooth_vibes') {
      base.boxShadow = `0 10px 40px ${colors.primary}20`;
    } else {
      base.boxShadow = '0 4px 15px rgba(0,0,0,0.08)';
    }
    
    if (rotate && (template === 'fresh_start' || template === 'bold_energy')) {
      base.transform = 'rotate(-1deg)';
    }
    
    return base;
  };

  const getButtonStyle = (variant = 'primary') => {
    const base = {
      fontFamily: "'Fredoka', sans-serif",
      fontWeight: 'bold',
      borderRadius: template === 'smooth_vibes' ? '1.5rem' : '1rem',
      transition: 'all 0.2s ease'
    };
    
    if (variant === 'primary') {
      base.backgroundColor = colors.secondary;
      base.color = 'white';
    }
    
    if (template === 'street_smart' || template === 'bold_energy') {
      base.border = `2px solid ${colors.text}`;
    }
    
    return base;
  };

  return (
    <div className="min-h-screen pb-24" style={{ 
      backgroundColor: colors.background,
      fontFamily: "'Quicksand', sans-serif"
    }}>
      {/* Birthday Animation */}
      {isBirthday && (
        <BirthdayAnimation userName={userName || user?.name} onClose={dismissBirthday} />
      )}
      
      {/* Profile Completion Modal (first-login flow) */}
      <ProfileCompletionModal user={user} onComplete={(updatedUser) => setUser(updatedUser)} />
      
      {/* Mandatory Weekly Check-in Modal */}
      <MandatoryCheckinModal user={user} onComplete={() => fetchDashboardData()} />
      
      {/* Goal Reminders */}
      <GoalReminders user={user} />
      
      {/* Hero Header */}
      <div className="relative overflow-hidden" style={{ background: `linear-gradient(135deg, #1B2D4F 0%, #2A4470 100%)` }}>
        {/* Decorative elements based on template */}
        {template === 'fresh_start' && (
          <>
            <div className="absolute top-[-50px] right-[-30px] w-40 h-40 rounded-full blur-xl" 
                 style={{ backgroundColor: colors.secondary, opacity: 0.3 }} />
            <div className="absolute bottom-10 left-[-40px] w-32 h-32 rounded-full blur-lg"
                 style={{ backgroundColor: colors.accent, opacity: 0.4 }} />
          </>
        )}
        {template === 'street_smart' && (
          <div className="absolute top-0 right-0 w-48 h-48"
               style={{ background: colors.secondary, clipPath: 'polygon(100% 0, 0 0, 100% 100%)', opacity: 0.9 }} />
        )}
        {template === 'smooth_vibes' && (
          <>
            <div className="absolute top-16 right-8 w-24 h-24 rounded-full bg-white/10 backdrop-blur-md" />
            <div className="absolute top-32 left-4 w-16 h-16 rounded-full bg-white/15 backdrop-blur-sm" />
          </>
        )}
        {template === 'bold_energy' && (
          <>
            <div className="absolute top-8 right-4 w-16 h-16 rounded-2xl border-2 flex items-center justify-center"
                 style={{ backgroundColor: colors.accent, borderColor: colors.text, transform: 'rotate(12deg)' }}>
              <Star className="w-8 h-8" style={{ color: colors.text }} fill="currentColor" />
            </div>
          </>
        )}

        <div className="relative px-4 pt-5 pb-6">
          <div className="max-w-md mx-auto">
            {/* Top Bar */}
            <div className="flex justify-between items-center mb-6">
              <div className="flex items-center gap-3">
                <div className="w-14 h-14 bg-white/10 rounded-xl p-1 overflow-hidden">
                  <img src={logo} alt="Logo" className="w-full h-full object-contain" />
                </div>
                <div className="text-white">
                  <p className="text-xs font-semibold opacity-80">{programName}</p>
                  <p className="text-sm font-bold">{new Date().getFullYear()}</p>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <NotificationCenter user={user} />
                <button 
                  onClick={handleLogout}
                  className="bg-white/20 backdrop-blur rounded-xl p-2.5 hover:bg-white/30 transition-colors"
                >
                  <LogOut className="w-5 h-5 text-white" />
                </button>
              </div>
            </div>

            {/* Welcome Section */}
            <div className="flex items-center gap-4 mb-6">
              <div className="w-20 h-20 rounded-2xl bg-white flex items-center justify-center shadow-xl"
                   style={template === 'fresh_start' ? { transform: 'rotate(-3deg)' } : 
                          template === 'street_smart' || template === 'bold_energy' ? 
                          { border: `3px solid ${colors.secondary}` } : {}}>
                <Avatar className="w-16 h-16">
                  {user.profile_picture ? (
                    <AvatarImage src={user.profile_picture} alt={user.name} />
                  ) : null}
                  <AvatarFallback 
                    className="text-2xl font-bold"
                    style={{ backgroundColor: `${colors.primary}20`, color: colors.primary, fontFamily: "'Fredoka', sans-serif" }}
                  >
                    {user.avatar_emoji || user.name.charAt(0)}
                  </AvatarFallback>
                </Avatar>
              </div>
              <div>
                <h1 className="text-2xl font-bold text-white" style={{ fontFamily: "'Fredoka', sans-serif" }}>
                  {template === 'street_smart' || template === 'bold_energy' 
                    ? `YO, ${user.name.split(' ')[0].toUpperCase()}!` 
                    : `Hey, ${user.name.split(' ')[0]}!`}
                </h1>
                <p className="text-white/70 text-sm">
                  {template === 'bold_energy' ? "Let's get this bread" : "Ready to level up today?"}
                </p>
                {user.program_type && (
                  <span className="inline-block mt-2 text-xs font-bold px-3 py-1 rounded-full"
                        style={{ backgroundColor: 'rgba(232,108,61,0.2)', color: '#FF8A5C', border: '1px solid rgba(232,108,61,0.3)' }}>
                    {user.program_type === 'upnext' ? 'UpNEXT SYEP' : 'NextGEN Leader'}
                  </span>
                )}
              </div>
            </div>

            {/* Worksite Info */}
            {user.work_site && (
              <div className="bg-white/15 backdrop-blur rounded-xl p-3 mb-4"
                   style={template === 'fresh_start' ? { transform: 'rotate(1deg)' } : {}}>
                <div className="flex items-center gap-3">
                  <div className="w-9 h-9 rounded-lg flex items-center justify-center"
                       style={{ backgroundColor: colors.secondary }}>
                    <MapPin className="w-4 h-4 text-white" />
                  </div>
                  <div>
                    <p className="text-xs font-semibold text-white/70">Your Spot</p>
                    <p className="font-bold text-sm text-white">{user.work_site}</p>
                  </div>
                </div>
              </div>
            )}

            {/* Stats Grid */}
            <div className="grid grid-cols-2 gap-3">
              {/* Points Card */}
              <div style={getCardStyle(true)} className="p-4">
                <div className="flex items-center gap-2 mb-1">
                  <div className="w-7 h-7 rounded-lg flex items-center justify-center"
                       style={{ backgroundColor: `${colors.secondary}20` }}>
                    <Star className="w-4 h-4" style={{ color: colors.secondary }} fill="currentColor" />
                  </div>
                  <span className="text-xs font-bold uppercase" style={{ color: colors.primary }}>Points</span>
                </div>
                <p className="text-3xl font-bold" style={{ color: colors.text, fontFamily: "'Fredoka', sans-serif" }}>
                  {user.total_points?.toLocaleString() || 0}
                </p>
              </div>

              {/* Streak Card */}
              <div style={getCardStyle(template === 'fresh_start')} className="p-4">
                <div className="flex items-center gap-2 mb-1">
                  <div className="w-7 h-7 rounded-lg flex items-center justify-center"
                       style={{ backgroundColor: `${colors.accent}30` }}>
                    <Flame className="w-4 h-4" style={{ color: colors.secondary }} />
                  </div>
                  <span className="text-xs font-bold uppercase" style={{ color: colors.primary }}>Streak</span>
                </div>
                <p className="text-3xl font-bold" style={{ color: colors.text, fontFamily: "'Fredoka', sans-serif" }}>
                  {user.current_streak || 0}
                  <span className="text-lg" style={{ color: colors.text_muted }}> {(user.current_streak || 0) === 1 ? 'day' : 'days'}</span>
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Bottom wave/edge */}
        {template === 'smooth_vibes' && (
          <div className="absolute -bottom-1 left-0 right-0 h-8"
               style={{ background: colors.background, borderRadius: '50% 50% 0 0 / 100% 100% 0 0' }} />
        )}
        {template === 'fresh_start' && (
          <svg className="absolute bottom-0 left-0 right-0" viewBox="0 0 1440 40" fill="none">
            <path d="M0 40V20C240 5 480 0 720 10C960 20 1200 35 1440 20V40H0Z" fill={colors.background} />
          </svg>
        )}
        {(template === 'street_smart' || template === 'bold_energy') && (
          <div className="h-4" style={{ backgroundColor: colors.background }} />
        )}
      </div>

      {/* Main Content */}
      <div className="max-w-md mx-auto px-4 py-4 space-y-5">
        
        {/* AI Guide */}
        {aiGuidance?.message && (
          <div className="rounded-2xl p-4" style={{ background: 'linear-gradient(135deg, #EDE9FE 0%, #FEF3C7 100%)', border: '1px solid #DDD6FE' }}>
            <div className="flex gap-3">
              <div className="w-9 h-9 rounded-xl flex items-center justify-center flex-shrink-0" style={{ backgroundColor: colors.secondary }}>
                <Brain className="w-5 h-5 text-white" />
              </div>
              <div className="flex-1">
                <p className="text-xs font-bold text-violet-800 mb-0.5">Your AI Guide</p>
                <p className="text-sm text-gray-700 leading-relaxed">{aiGuidance.message}</p>
                {aiGuidance.celebration && (
                  <div className="mt-2 flex items-center gap-2 p-2 rounded-lg bg-green-50 border border-green-200">
                    <Trophy className="w-4 h-4 text-green-600 flex-shrink-0" />
                    <p className="text-xs text-green-700 font-medium">{aiGuidance.celebration}</p>
                  </div>
                )}
                {aiGuidance.suggestions?.length > 0 && (
                  <div className="mt-2 space-y-1">{aiGuidance.suggestions.map((s, i) => (
                    <button key={i} onClick={() => navigate(s.action)} className="flex items-center gap-2 w-full p-2 rounded-lg bg-white/80 hover:bg-white transition-all text-left">
                      <ChevronRight className="w-3 h-3 text-violet-500 flex-shrink-0" />
                      <span className="text-xs text-gray-700">{s.text}</span>
                      {s.points > 0 && <span className="ml-auto text-[9px] font-bold text-amber-600">+{s.points} pts</span>}
                    </button>
                  ))}</div>
                )}
              </div>
            </div>
          </div>
        )}

        {/* My Mentor Card */}
        {user.lead_monitor_name && (
          <div className="rounded-2xl overflow-hidden border" style={{ borderColor: colors.border, backgroundColor: colors.card }}>
            <div className="p-4 flex items-center gap-3">
              <div className="w-12 h-12 rounded-xl flex items-center justify-center flex-shrink-0" style={{ backgroundColor: `${colors.secondary}20` }}>
                {user.lead_monitor_picture ? (
                  <img src={user.lead_monitor_picture} alt="" className="w-12 h-12 rounded-xl object-cover" />
                ) : (
                  <Users className="w-6 h-6" style={{ color: colors.secondary }} />
                )}
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-[10px] font-bold uppercase tracking-wider" style={{ color: colors.secondary }}>Your {user.staff_role_label || 'Program Mentor'}</p>
                <p className="text-sm font-bold truncate" style={{ color: colors.text }}>{user.lead_monitor_name}</p>
                {user.lead_monitor_email && <p className="text-[10px] truncate" style={{ color: colors.text_muted }}>{user.lead_monitor_email}</p>}
                {user.lead_monitor_phone && <p className="text-[10px]" style={{ color: colors.text_muted }}>{user.lead_monitor_phone}</p>}
              </div>
              <button onClick={() => navigate('/inbox')} className="px-3 py-2 rounded-xl text-xs font-bold text-white flex-shrink-0" style={{ backgroundColor: colors.primary }}>
                <MessageSquare className="w-4 h-4" />
              </button>
            </div>
          </div>
        )}

        {/* Quick Actions */}
        <div className="flex gap-3 overflow-x-auto pb-2 -mx-4 px-4" style={{ WebkitOverflowScrolling: 'touch' }}>
          {[
            { icon: Lightbulb, label: 'Interests', path: '/interests' },
            { icon: Award, label: 'Skills', path: '/skills' },
            { icon: BookOpen, label: 'Courses', path: '/workshops' },
            { icon: Brain, label: 'AI Guide', path: '/ai-guide' },
            { icon: Star, label: 'Assess', path: '/custom-assessments' },
            { icon: Calendar, label: 'Events', path: '/events' },
            { icon: FolderOpen, label: 'Vault', path: '/vault' },
            { icon: Briefcase, label: 'Project', path: '/summer-project' },
            { icon: FolderOpen, label: 'Resources', path: '/resources' },
            { icon: Trophy, label: 'Rankings', path: '/leaderboard' },
            { icon: Users, label: 'People', path: '/people-like-you' },
          ].map((item, idx) => (
            <button
              key={item.label}
              onClick={() => navigate(item.path)}
              className="flex-shrink-0 p-3 min-w-[75px] transition-all hover:scale-105"
              style={{
                ...getCardStyle(),
              }}
            >
              <div className="w-9 h-9 rounded-lg mx-auto mb-2 flex items-center justify-center"
                   style={{ backgroundColor: idx % 2 === 0 ? `${colors.primary}15` : `${colors.secondary}15` }}>
                <item.icon className="w-5 h-5" style={{ color: idx % 2 === 0 ? colors.primary : colors.secondary }} />
              </div>
              <p className="text-xs font-bold text-center" style={{ color: colors.text }}>{item.label}</p>
            </button>
          ))}
        </div>

        {/* AI Weekly Trends */}
        {weeklyTrends?.has_data && (
          <Card className="p-4 overflow-hidden relative" style={{ ...getCardStyle(), borderColor: `${colors.primary}20` }}>
            <div className="relative">
              <div className="flex items-center gap-2 mb-2">
                <div className="w-7 h-7 rounded-lg flex items-center justify-center" style={{ backgroundColor: `${colors.primary}15` }}>
                  <Sparkles className="w-3.5 h-3.5" style={{ color: colors.primary }} />
                </div>
                <h3 className="font-bold text-sm" style={{ color: colors.text }}>Your AI Insights</h3>
                {weeklyTrends.mood_emoji && <span className="text-base">{weeklyTrends.mood_emoji}</span>}
              </div>
              <p className="text-sm leading-relaxed mb-2" style={{ color: colors.text_secondary }}>{weeklyTrends.insight}</p>
              {weeklyTrends.encouragement && (
                <p className="text-sm font-medium px-3 py-2 rounded-xl" style={{ backgroundColor: `${colors.primary}10`, color: colors.primary }}>{weeklyTrends.encouragement}</p>
              )}
            </div>
          </Card>
        )}

        {/* First Goal Nudge — only shows if user has no goals */}
        {user && (!user.goals_count || user.goals_count === 0) && (
          <Card className="p-4 cursor-pointer hover:shadow-md transition-all" style={getCardStyle()} onClick={() => navigate('/goals')}>
            <div className="flex items-center gap-3">
              <div className="w-11 h-11 rounded-xl flex items-center justify-center" style={{ background: 'linear-gradient(135deg, #F59E0B, #E86C3D)' }}>
                <Target className="w-5 h-5 text-white" />
              </div>
              <div className="flex-1">
                <p className="font-bold text-sm" style={{ color: colors.text }}>Set Your First Goal</p>
                <p className="text-xs" style={{ color: colors.text_muted }}>Earn 25 points for each goal you set. Start now!</p>
              </div>
              <ChevronRight className="w-5 h-5" style={{ color: colors.text_muted }} />
            </div>
          </Card>
        )}

        {/* Course Progress Nudge */}
        {staleCourses.length > 0 && (
          <Card className="p-4" style={getCardStyle()}>
            <div className="flex items-center gap-2 mb-3">
              <div className="w-7 h-7 rounded-lg flex items-center justify-center" style={{ backgroundColor: `${colors.secondary}15` }}>
                <BookOpen className="w-3.5 h-3.5" style={{ color: colors.secondary }} />
              </div>
              <h3 className="font-bold text-sm" style={{ color: colors.text }}>Continue Where You Left Off</h3>
            </div>
            <div className="space-y-2">
              {staleCourses.map(course => (
                <div key={course.id} onClick={() => navigate(`/course/${course.id}`)} className="flex items-center gap-3 p-3 rounded-xl cursor-pointer hover:shadow-sm transition-all" style={{ backgroundColor: `${colors.secondary}08` }}>
                  <div className="w-9 h-9 rounded-xl flex items-center justify-center flex-shrink-0" style={{ backgroundColor: `${colors.secondary}15` }}>
                    <Play className="w-4 h-4" style={{ color: colors.secondary }} fill="currentColor" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-semibold truncate" style={{ color: colors.text }}>{course.title}</p>
                    <p className="text-xs" style={{ color: colors.text_muted }}>Module {course.current_module} of {course.total_modules}</p>
                  </div>
                  <Progress value={(course.current_module / course.total_modules) * 100} className="w-16 h-1.5" />
                </div>
              ))}
            </div>
          </Card>
        )}

        {/* Events Card - Prominent */}
        <div onClick={() => navigate('/events')} className="p-4 cursor-pointer hover:shadow-md transition-all"
          style={{
            background: 'linear-gradient(135deg, #4F46E5, #7C3AED)',
            borderRadius: template === 'smooth_vibes' ? '1.5rem' : '1rem',
          }}>
          <div className="flex items-center gap-3">
            <div className="w-11 h-11 bg-white/20 rounded-xl flex items-center justify-center">
              <Calendar className="w-5 h-5 text-white" />
            </div>
            <div className="flex-1 text-white">
              <h3 className="font-bold text-sm" style={{ fontFamily: "'Fredoka', sans-serif" }}>Events & Orientation</h3>
              <p className="text-white/70 text-xs">Schedules, check-ins & surveys</p>
            </div>
            <ChevronRight className="w-5 h-5 text-white/60" />
          </div>
        </div>

        {/* Win Wall Card */}
        <div className="p-5 relative overflow-hidden" 
             style={{ 
               backgroundColor: colors.secondary, 
               borderRadius: template === 'smooth_vibes' ? '1.5rem' : '1rem',
               border: template === 'street_smart' || template === 'bold_energy' ? `2px solid ${colors.text}` : 'none',
               boxShadow: template === 'bold_energy' ? '4px 4px 0px 0px rgba(0,0,0,0.9)' : undefined,
               transform: template === 'fresh_start' ? 'rotate(-1deg)' : undefined
             }}>
          <div className="absolute top-[-20px] right-[-20px] w-24 h-24 rounded-full bg-white/20" />
          <div className="relative">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-11 h-11 bg-white rounded-xl flex items-center justify-center shadow-md"
                   style={template === 'street_smart' || template === 'bold_energy' ? { border: `2px solid ${colors.text}` } : {}}>
                <Sparkles className="w-5 h-5" style={{ color: colors.secondary }} />
              </div>
              <div className="text-white">
                <h2 className="text-lg font-bold" style={{ fontFamily: "'Fredoka', sans-serif" }}>Win Wall</h2>
                <p className="text-white/80 text-sm">Share your weekly wins</p>
              </div>
            </div>
            <button 
              onClick={() => navigate('/wins')}
              className="w-full py-3.5 font-bold text-base"
              style={{
                backgroundColor: 'white',
                color: colors.secondary,
                borderRadius: template === 'smooth_vibes' ? '1rem' : '0.75rem',
                fontFamily: "'Fredoka', sans-serif",
                border: template === 'street_smart' || template === 'bold_energy' ? `2px solid ${colors.text}` : 'none'
              }}
            >
              Drop a Win! +50 pts
            </button>
          </div>
        </div>

        {/* This Week Section */}
        <div>
          <div className="flex items-center gap-2 mb-3">
            <div className="w-8 h-8 rounded-lg flex items-center justify-center"
                 style={{ backgroundColor: `${colors.primary}15` }}>
              <Calendar className="w-4 h-4" style={{ color: colors.primary }} />
            </div>
            <h2 className="text-base font-bold" style={{ color: colors.text, fontFamily: "'Fredoka', sans-serif" }}>
              This Week
            </h2>
          </div>

          <div className="space-y-3">
            {/* Assessment Card */}
            {!assessments.pre && (
              <div 
                onClick={() => navigate('/skills-tracker', { state: { type: 'pre' } })}
                className="p-4 cursor-pointer transition-all hover:scale-[1.01]"
                style={{
                  background: `linear-gradient(135deg, ${colors.primary} 0%, ${colors.primary_light} 100%)`,
                  borderRadius: template === 'smooth_vibes' ? '1rem' : '0.75rem',
                  border: template === 'street_smart' || template === 'bold_energy' ? `2px solid ${colors.text}` : 'none',
                  boxShadow: template === 'bold_energy' ? '3px 3px 0px 0px rgba(0,0,0,0.9)' : undefined
                }}
              >
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-xl bg-white/20 flex items-center justify-center">
                      <Brain className="w-5 h-5 text-white" />
                    </div>
                    <div className="text-white">
                      <h3 className="font-bold" style={{ fontFamily: "'Fredoka', sans-serif" }}>Skills Tracker</h3>
                      <p className="text-white/80 text-sm">Level up • 50 pts</p>
                    </div>
                  </div>
                  <span className="text-xs font-bold px-3 py-1 rounded-full"
                        style={{ backgroundColor: colors.secondary, color: 'white' }}>
                    New
                  </span>
                </div>
              </div>
            )}

            {/* Check-in Card */}
            <div 
              onClick={() => !weeklyStatus.checkin && navigate('/checkin')}
              className="p-4 cursor-pointer transition-all hover:scale-[1.01]"
              style={getCardStyle()}
            >
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="w-9 h-9 rounded-lg flex items-center justify-center"
                       style={{ backgroundColor: weeklyStatus.checkin ? `${colors.accent}30` : `${colors.primary}15` }}>
                    <CheckCircle className="w-5 h-5" 
                                 style={{ color: weeklyStatus.checkin ? colors.accent : colors.primary }} />
                  </div>
                  <div>
                    <h3 className="font-bold" style={{ color: colors.text, fontFamily: "'Fredoka', sans-serif" }}>
                      {weeklyStatus.checkin ? 'Check-in Done!' : 'Weekly Check-in'}
                    </h3>
                    <p className="text-sm" style={{ color: colors.text_muted }}>
                      {weeklyStatus.checkin ? 'See you next week' : 'How\'s your week? • 20 pts'}
                    </p>
                  </div>
                </div>
                {weeklyStatus.checkin ? (
                  <div className="w-7 h-7 rounded-full flex items-center justify-center"
                       style={{ backgroundColor: colors.accent }}>
                    <CheckCircle className="w-4 h-4 text-white" />
                  </div>
                ) : (
                  <ChevronRight className="w-5 h-5" style={{ color: colors.text_muted }} />
                )}
              </div>
            </div>

            {/* Trivia Card */}
            <div 
              onClick={() => navigate('/trivia')}
              className="p-4 cursor-pointer transition-all hover:scale-[1.01]"
              style={getCardStyle()}
            >
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="w-9 h-9 rounded-lg flex items-center justify-center"
                       style={{ backgroundColor: `${colors.secondary}15` }}>
                    <Zap className="w-5 h-5" style={{ color: colors.secondary }} />
                  </div>
                  <div>
                    <h3 className="font-bold" style={{ color: colors.text, fontFamily: "'Fredoka', sans-serif" }}>
                      Weekly Trivia
                    </h3>
                    <p className="text-sm" style={{ color: colors.text_muted }}>Test yourself • 25 pts</p>
                  </div>
                </div>
                <ChevronRight className="w-5 h-5" style={{ color: colors.text_muted }} />
              </div>
            </div>
          </div>
        </div>

        {/* Team Card */}
        <div className="p-5 relative overflow-hidden" style={getCardStyle()}>
          <div className="absolute top-0 right-0 w-20 h-20 rounded-full blur-xl"
               style={{ backgroundColor: colors.accent, opacity: 0.3 }} />
          <div className="relative">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl flex items-center justify-center"
                     style={{ backgroundColor: colors.primary }}>
                  <Users className="w-5 h-5 text-white" />
                </div>
                <div>
                  <h3 className="font-bold" style={{ color: colors.text, fontFamily: "'Fredoka', sans-serif" }}>
                    Your Team
                  </h3>
                  <p className="text-sm" style={{ color: colors.text_muted }}>View teammates</p>
                </div>
              </div>
            </div>
            <button 
              onClick={() => navigate('/team')}
              className="w-full py-2.5 font-bold text-sm"
              style={{
                backgroundColor: `${colors.primary}15`,
                color: colors.primary,
                borderRadius: '0.75rem',
                fontFamily: "'Fredoka', sans-serif"
              }}
            >
              See Your Squad <ChevronRight className="w-4 h-4 inline ml-1" />
            </button>
          </div>
        </div>
      </div>

      {/* Bottom Navigation */}
      <div className="fixed bottom-0 left-0 right-0 backdrop-blur-lg border-t px-4 py-2 z-50"
           style={{ backgroundColor: 'rgba(27,45,79,0.97)', borderColor: 'rgba(255,255,255,0.08)' }}>
        <div className="max-w-md mx-auto flex justify-around items-center">
          {[
            { icon: Target, label: 'Home', path: '/dashboard', active: true },
            { icon: Target, label: 'Goals', path: '/goals', active: false },
            { icon: MessageSquare, label: 'Inbox', path: '/inbox', active: false },
            { icon: Trophy, label: 'Ranks', path: '/leaderboard', active: false },
            { icon: Users, label: 'Profile', path: '/profile', active: false },
          ].map((item) => (
            <button 
              key={item.label}
              onClick={() => navigate(item.path)}
              className="flex flex-col items-center gap-1 px-3 py-2 rounded-xl transition-all"
              style={{ 
                backgroundColor: item.active ? '#FFF0EA' : 'transparent',
                color: item.active ? '#E86C3D' : 'rgba(255,255,255,0.4)'
              }}
            >
              <item.icon className="w-5 h-5" />
              <span className="text-xs font-bold" style={{ fontFamily: "'Fredoka', sans-serif" }}>
                {item.label}
              </span>
            </button>
          ))}
        </div>
      </div>
    </div>
  );
};

export default ThemedYouthDashboard;
